import { Link, useParams } from 'react-router-dom';
import { formatDrinkDisplayName } from '@/lib/displayName';
import { formatMetric, formatMoney, mlToFlOz } from '@/lib/metrics';
import { formatPackagePrice } from '@/lib/packageLabel';
import {
  categoryMedians,
  vsMedianPhrase,
} from '@/lib/categoryStats';
import { useCatalog } from '@/hooks/CatalogContext';

export function DrinkDetailPage() {
  const { slug } = useParams();
  const {
    loading,
    getRowBySlug,
    dataset,
    prefs,
    setBaseline,
    baselineId,
    rows,
  } = useCatalog();

  if (loading) return <p>Loading…</p>;
  const row = slug ? getRowBySlug(slug) : undefined;
  if (!row) {
    return (
      <p role="alert">
        Drink not found. <Link to="/">Back to catalog</Link>
      </p>
    );
  }

  const sources = dataset?.sources ?? [];
  const recipe = dataset?.recipes.find((r) => r.productId === row.product.id);
  const abvSource = sources.find((s) => s.id === row.product.abvSourceId);
  const nutSource = sources.find((s) => s.id === row.nutrition?.sourceId);
  const unit = prefs.volumeUnit;
  const med = categoryMedians(rows, row.product.category);
  const calVs = vsMedianPhrase(
    row.alcoholPer100Calories,
    med.alcoholPer100Calories,
    true,
  );
  const dolVs = vsMedianPhrase(
    row.alcoholPerDollar,
    med.alcoholPerDollar,
    true,
  );

  return (
    <article>
      <p className="eyebrow">{row.product.category}</p>
      <h1>{formatDrinkDisplayName(row.product.name)}</h1>
      <p className="muted">
        {row.product.brand !== 'Recipe'
          ? `${formatDrinkDisplayName(row.product.brand)} · `
          : ''}
        {row.product.subtype} · market {row.product.market}
      </p>

      <section className="panel prose-page">
        <h2>How to read this page</h2>
        <p>
          Figures below describe composition and package value for this product —
          not taste, health grades, or predicted intoxication. Alcohol / 100 cal
          answers calorie efficiency; alcohol / $ answers package value for the
          selected offer. Missing values stay blank rather than zero. See{' '}
          <Link to="/guides/how-to-read-boozeratio-rankings/">
            how to read rankings
          </Link>{' '}
          and <Link to="/methodology/">methodology</Link>. For adults of legal
          drinking age.
        </p>
        {(calVs || dolVs) && (
          <ul>
            {calVs && (
              <li>
                Alcohol / 100 cal vs {row.product.category} median ({med.sampleSizeCal}{' '}
                priced-nutrition rows): {calVs}.
              </li>
            )}
            {dolVs && (
              <li>
                Alcohol / $ vs {row.product.category} median ({med.sampleSizeDol}{' '}
                priced rows): {dolVs}.
              </li>
            )}
          </ul>
        )}
      </section>

      <section className="panel">
        <h2>Serving nutrition</h2>
        <ul>
          <li>
            Serving:{' '}
            {row.nutrition
              ? unit === 'fl_oz'
                ? `${formatMetric(mlToFlOz(row.nutrition.servingMl), 2)} fl oz`
                : `${formatMetric(row.nutrition.servingMl, 1)} mL`
              : '—'}
          </li>
          <li>ABV: {row.product.abvPercent ?? 'unknown'}%</li>
          <li>
            Calories: {row.nutrition?.calories ?? 'unknown'} (
            {row.nutrition?.status ?? 'unknown'})
          </li>
          <li>Carbs: {row.nutrition?.carbsG ?? '—'} g</li>
          <li>Sugar: {row.nutrition?.sugarG ?? '—'} g</li>
          {row.nutrition?.estimationMethod && (
            <li>Estimation: {row.nutrition.estimationMethod}</li>
          )}
        </ul>
      </section>

      <section className="panel">
        <h2>Normalized metrics</h2>
        <ul>
          <li>
            Pure alcohol / serving:{' '}
            {formatMetric(
              unit === 'fl_oz'
                ? row.serving.pureAlcoholFlOz
                : row.serving.pureAlcoholMl,
            )}{' '}
            {unit === 'fl_oz' ? 'fl oz' : 'mL'}
          </li>
          <li>
            Standard drinks: {formatMetric(row.serving.standardDrinks, 2)}
          </li>
          <li>
            Alcohol ({unit === 'fl_oz' ? 'fl oz' : 'mL'}) / 100 cal:{' '}
            {formatMetric(
              unit === 'fl_oz'
                ? row.serving.alcoholPer100CaloriesFlOz
                : row.alcoholPer100Calories,
            )}
          </li>
          <li>
            Calories / standard drink:{' '}
            {formatMetric(row.caloriesPerStandardDrink, 1)}
          </li>
          <li>
            Alcohol ({unit === 'fl_oz' ? 'fl oz' : 'mL'}) / ${' '}
            {formatMetric(
              unit === 'fl_oz'
                ? row.package.alcoholPerDollarFlOz
                : row.alcoholPerDollar,
            )}
          </li>
          <li>
            $ / standard drink:{' '}
            {row.dollarsPerStandardDrink != null
              ? formatMoney(row.dollarsPerStandardDrink)
              : '—'}
          </li>
          <li>
            Package price:{' '}
            {formatPackagePrice(
              row.price.packagePriceUsd,
              row.price.containerCount,
              row.price.containerMl,
              { category: row.product.category, volumeUnit: unit },
            )}
          </li>
        </ul>
        <p className="muted">
          Ratios compare composition and value, not intoxication. Edit package
          prices on the <Link to={`/compare/?c=${row.product.id}`}>compare</Link>{' '}
          page.
        </p>
      </section>

      {row.offers.length > 0 && (
        <section className="panel">
          <h2>Package offers</h2>
          <ul>
            {row.offers.map((o) => (
              <li key={o.id}>
                {formatPackagePrice(
                  o.salePrice ?? o.regularPrice,
                  o.containerCount,
                  o.containerMl,
                  { category: row.product.category, volumeUnit: unit },
                )}{' '}
                · {o.priceContext}
                {o.isDefault ? ' (default)' : ''}
                {o.notes ? ` — ${o.notes}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      {recipe && (
        <section className="panel">
          <h2>Recipe (estimated)</h2>
          <p>{recipe.assumptions}</p>
          <ul>
            {recipe.ingredients.map((ing) => (
              <li key={ing.ingredientId + ing.label}>
                {ing.label}:{' '}
                {ing.amountMl != null
                  ? `${formatMetric(ing.amountMl, 1)} mL`
                  : ''}
                {ing.amountG != null ? `${ing.amountG} g` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel">
        <h2>Sources</h2>
        <ul>
          {abvSource && (
            <li>
              ABV: <a href={abvSource.url}>{abvSource.publisher}</a> (
              {abvSource.retrievedAt})
            </li>
          )}
          {nutSource && (
            <li>
              Nutrition: <a href={nutSource.url}>{nutSource.publisher}</a> (
              {nutSource.retrievedAt})
            </li>
          )}
          <li>
            Price: {row.price.label}
            {row.price.region ? ` · ${row.price.region}` : ''}
            {' · '}
            {formatPackagePrice(
              row.price.packagePriceUsd,
              row.price.containerCount,
              row.price.containerMl,
              { category: row.product.category, volumeUnit: unit },
            )}
          </li>
        </ul>
      </section>

      <div className="row-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() =>
            setBaseline(
              baselineId === row.product.id ? null : row.product.id,
            )
          }
        >
          {baselineId === row.product.id
            ? 'Clear baseline'
            : 'Set as baseline'}
        </button>
        <Link className="btn" to={`/compare/?c=${row.product.id}`}>
          Compare
        </Link>
        <Link className="btn" to="/">
          Catalog
        </Link>
      </div>
    </article>
  );
}
